import { h } from 'preact';
import { useState, useEffect, useMemo, useCallback } from 'preact/hooks';
import useResponsive from 'utils/responsive';
import { Thumbs, FreeMode, Navigation, EffectFade, Scrollbar, Zoom } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import Image from 'utils/image';
import Badge from 'utils/badge';
import AngleUpIcon from 'utils/icon/icon-angle-up.svg';
import Highlights from './product-highlights';
import 'swiper/css/zoom';

const ProductGallery = ({ product, filteredVariants }) => {
  const { isGiftCard, highlights, badge } = product;
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [currentImages, setCurrentImages] = useState([]);
  const [gallerySwiper, setGallerySwiper] = useState(null);
  const { isMobile } = useResponsive();

  const getVariantImages = useCallback(
    (variant) => {
      if (!isGiftCard) {
        if (variant.images.length > 0) {
          return variant.images;
        }
        const colorOption = variant.selectedOptions?.find((o) => o.name.toLowerCase() === 'color')?.value;
        if (!colorOption) return [];

        // Find images based on color option
        const variantWithMatchingColor = product.variants.find((v) => {
          const vColorOption = v.selectedOptions?.find((o) => o.name.toLowerCase() === 'color')?.value;
          return vColorOption === colorOption && v.images.length > 0;
        });

        return variantWithMatchingColor?.images || [];
      }
      return variant.featuredImage ? [variant.featuredImage] : [];
    },
    [isGiftCard, product.variants],
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const variantId = params.get('variant');

    if (variantId) {
      const variant = filteredVariants.find((v) => v.id.includes(variantId));
      if (variant) {
        const images = getVariantImages(variant);
        setCurrentImages(images);
      }
    } else if (filteredVariants.length > 0) {
      setCurrentImages(getVariantImages(filteredVariants[0]));
    }
  }, [filteredVariants]);

  useEffect(() => {
    const handleVariantUpdate = (event) => {
      if (!event.detail?.currentVariant) return;

      const variant = filteredVariants.find((v) => v.id === event.detail.currentVariant.id);

      if (variant) {
        const images = getVariantImages(variant);
        setCurrentImages(images);

        if (gallerySwiper) {
          gallerySwiper.slideTo(0);
          gallerySwiper.update();
        }
        if (thumbsSwiper) {
          thumbsSwiper.update();
        }
      }
    };

    document.addEventListener('currentVariantUpdated', handleVariantUpdate);
    return () => {
      document.removeEventListener('currentVariantUpdated', handleVariantUpdate);
    };
  }, [filteredVariants, thumbsSwiper, gallerySwiper, getVariantImages]);

  useEffect(() => {
    if (gallerySwiper && thumbsSwiper) {
      gallerySwiper.thumbs.swiper = thumbsSwiper;
      gallerySwiper.thumbs.init();
      gallerySwiper.thumbs.update();

      if (thumbsSwiper.params.navigation) {
        gallerySwiper.params.navigation.prevEl = '.swiper-button-prev-custom';
        gallerySwiper.params.navigation.nextEl = '.swiper-button-next-custom';
        gallerySwiper.navigation.init();
        gallerySwiper.navigation.update();
      }

      thumbsSwiper.slideTo(0);
    }
  }, [currentImages, gallerySwiper, thumbsSwiper]);

  const highlightsComponent = useMemo(
    () => (highlights?.length ? <Highlights data={highlights} /> : null),
    [highlights],
  );

  const showNavigationButtons = !isMobile && currentImages.length > 5;
  const navigationConfig = {
    nextEl: '.swiper-button-next-custom',
    prevEl: '.swiper-button-prev-custom',
  };

  useEffect(() => {
    if (isGiftCard && Array.isArray(currentImages[0])) {
      const flattenedImages = currentImages.flat();
      setCurrentImages(flattenedImages);
    }
  }, [isGiftCard, currentImages]);

  return (
    <div className="top-15 flex md:mx-auto md:max-w-[50%] md:basis-1/2 md:pl-15 l:pl-20 lg:max-w-[784px] lg:basis-full">
      {!isMobile && (
        <div
          className={`thumbs relative mr-[10px] hidden md:block md:w-[60px] md:min-w-[60px]
            l:w-[105px] l:min-w-[105px] ${
              showNavigationButtons ? 'overflow-hidden pb-[50px] pt-[50px] md:h-[307px] l:h-[560px]' : ''
            }`}
        >
          {showNavigationButtons && (
            <button className="swiper-button-prev-custom absolute left-0 top-0 z-10 flex w-full cursor-pointer items-center justify-center text-primary disabled:border-disabled disabled:text-disabled">
              <AngleUpIcon />
            </button>
          )}
          <Swiper
            className="thumb-swiper h-full w-full"
            direction="vertical"
            modules={[Navigation, Thumbs]}
            slidesPerView={4}
            slidesPerGroup={1}
            onSwiper={setThumbsSwiper}
            loop={false}
            navigation={navigationConfig}
          >
            {currentImages.map((img) => (
              <SwiperSlide
                className="mb-[10px] w-full cursor-pointer overflow-hidden rounded-[4px] hover:border hover:border-primary md:!h-[65px] l:!h-[105px] [&.swiper-slide-thumb-active]:border [&.swiper-slide-thumb-active]:border-primary"
                key={img.id}
              >
                <Image
                  {...img}
                  srcWidths={{ sm: 640, md: 768, lg: 1024, xl: 1280, '2xl': 1536 }}
                />
              </SwiperSlide>
            ))}
          </Swiper>
          {showNavigationButtons && (
            <button className="swiper-button-next-custom absolute bottom-0 left-0 z-10 flex w-full rotate-180 cursor-pointer items-center justify-center text-primary disabled:border-disabled disabled:text-disabled">
              <AngleUpIcon />
            </button>
          )}
        </div>
      )}
      <div className="mainslider l:max-w-auto relative w-full md:max-w-[calc(100%-70px)] lg:w-full lg:max-w-[669px]">
        {badge?.copy && (
          <Badge
            badge={badge}
            classes="left-12 top-12 md:left-20 md:top-20 md:body-14-med"
          />
        )}
        <Swiper
          className="gallery-swiper relative mb-20 !pb-0 after:absolute after:bottom-20 after:h-1 after:w-full after:bg-grey-5 after:content-[''] md:mx-auto md:after:hidden"
          modules={[Navigation, Thumbs, EffectFade, FreeMode, Scrollbar, Zoom]}
          thumbs={{ swiper: thumbsSwiper }}
          loop={false}
          slidesPerView={1}
          onSwiper={setGallerySwiper}
          scrollbar={{
            draggable: true,
            dragClass: 'swiper-scrollbar-drag bg-primary custom-swiper-scrollbar',
            horizontalClass: 'md:hidden',
          }}
          watchSlidesProgress={true}
          freeMode={true}
          breakpoints={{
            768: {
              freeMode: false,
              zoom: {
                maxRatio: 3,
              },
            },
          }}
          clickable={true}
        >
          {currentImages.map((img, index) => (
            <SwiperSlide
              className="mb-15 !w-full !max-w-full overflow-hidden border-b border-t border-grey-5 md:mb-0 md:rounded-[10px] md:border md:border-grey-5"
              key={img.id}
            >
              <div className="swiper-zoom-container w-full">
                <Image
                  {...img}
                  srcWidths={{ sm: 400, md: 768, lg: 1024, xl: 1280, '2xl': 1536 }}
                  classes="w-full overflow-visible"
                  zoom={true}
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        {highlightsComponent}
      </div>
    </div>
  );
};

export default ProductGallery;
